/* eslint-disable react/prop-types */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Mousetrap from 'mousetrap';

import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import CopyToClipboard from '../CopyToClipboard';
import Loader from '../../components/Loader';
import LoaderSkeleton from '../LoaderSkeleton';
import OverlayTriggerTooltip from '../OverlayTriggerTooltip';

import { useOutsideClick } from '../../helpers';

import SortIndicator from '../SortIndicator';

import {
  copySelectedCellValues,
  getCellDown,
  getCellLeft,
  getCellRight,
  getCellUp,
  getCopyTableValue,
  getSelectedCellRange,
  sortTypes,
  transformColumns,
} from './utilities/helpers';

import styles from './_index.module.scss';

/**
 * @typedef {Object} TableData
 * @property {import('./utilities/types').TableDataColumn[]} columns
 * @property {import('./utilities/types').TableDataShape['data']} data
 */

/**
 * @typedef {Object} TableDataProps
 * @property {import('react').ReactNode[]} [actionButtons]
 * @property {import('./utilities/types').TableDataColumn[]} columns
 * @property {boolean} [copyEnabled]
 * @property {import('./utilities/types').TableDataShape['data']} data
 * @property {boolean} [hasPlaceholders]
 * @property {boolean} [isLoading]
 * @property {boolean} [isUpdating]
 * @property {(table: TableData) => void} [onSortedTableDataChange]
 * @property {import('react').ReactNode} [prescriptText]
 * @property {() => import('react').ReactNode} [renderEmptyMessage]
 * @property {number} [rowHeight]
 * @property {import('react').Dispatch<import('react').SetStateAction<any>>} [setDownloadData]
 * @property {boolean} [shouldTruncateHeaders]
 * @property {number} [size]
 * @property {boolean} [stickyHeader]
 * @property {boolean} [useWindowScroll]
 */

/**
 * @param {TableDataProps} props
 */
const TableData = ({
  actionButtons = [],
  columns,
  copyEnabled = true,
  data,
  hasPlaceholders = false,
  isLoading,
  isUpdating,
  onSortedTableDataChange,
  prescriptText,
  renderEmptyMessage,
  rowHeight = 30,
  setDownloadData,
  shouldTruncateHeaders = true,
  size,
  stickyHeader = false,
  useWindowScroll = true,
}) => {
  const tableRef = useRef(null);
  const footerRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const [hoveredColumnId, setHoveredColumnId] = useState(null);
  const [sorting, setSorting] = useState([]);

  // Transform columns from react-table v7 format to TanStack v8 format
  const transformedColumns = useMemo(() => transformColumns(columns), [columns]);

  const rowGroupAccessor = useMemo(
    () => transformedColumns.find((column) => column.rowSpanBy)?.rowSpanBy ?? null,
    [transformedColumns]
  );

  const table = useReactTable({
    columns: transformedColumns,
    data,
    enableSorting: !rowGroupAccessor,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    sortingFns: sortTypes,
    state: { sorting },
  });

  const rows = table.getRowModel().rows;

  const [selectedCell, setSelectedCell] = useState(null); // for range selection
  const [rangeEndCell, setRangeEndCell] = useState(null);

  const clearSelectedCells = () => {
    setSelectedCell(null);
    setRangeEndCell(null);
  };

  useEffect(() => {
    // keyboard select
    if (selectedCell) {
      Mousetrap.bind(['up'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellUp({ selectedCell, table }));
      });
      Mousetrap.bind(['down'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellDown({ selectedCell, table }));
      });
      Mousetrap.bind(['left'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellLeft({ selectedCell, table }));
      });
      Mousetrap.bind(['right'], (e) => {
        e.preventDefault();
        return setSelectedCell(getCellRight({ selectedCell, table }));
      });
    }
  }, [selectedCell, table]);

  useEffect(() => {
    // copy-to-clipboard
    if (selectedCell || rangeEndCell) {
      Mousetrap.bind(['ctrl+c', 'command+c'], () => {
        copySelectedCellValues({ rangeEndCell, selectedCell, table });
      });
      return () => {
        Mousetrap.unbind(['ctrl+c', 'command+c']);
      };
    }
  }, [selectedCell, rangeEndCell, table]);

  useEffect(() => {
    // when the sort changes, cell selection is cleared and updates the download data with the sorted table data (only for Shopper Metrics .ppt)
    clearSelectedCells();
    onSortedTableDataChange?.({
      columns,
      data: rows.map((row) => row.original),
    });
    if (setDownloadData) {
      setDownloadData((prevData) => ({
        ...prevData,
        table: {
          columns,
          data: rows.map((row) => row.original),
        },
      }));
    }
  }, [sorting, columns, setDownloadData, rows]);

  useOutsideClick({
    onCallback: () => {
      clearSelectedCells();
    },
    ref: tableRef,
  });

  const hasFooter = table
    .getAllColumns()
    .some((col) => col.columnDef.footer !== undefined && col.columnDef.footer !== null);

  useEffect(() => {
    const headerEl = stickyHeaderRef.current;
    const footerEl = footerRef.current;
    const tableEl = tableRef.current;

    if (!tableEl || (!headerEl && !footerEl)) return;

    const handleScroll = () => {
      if (headerEl) headerEl.scrollLeft = tableEl.scrollLeft;
      if (footerEl) footerEl.scrollLeft = tableEl.scrollLeft;
    };

    tableEl.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      tableEl.removeEventListener('scroll', handleScroll);
    };
  }, [hasFooter, isLoading, stickyHeader]);

  const selectedRange = useMemo(
    () =>
      getSelectedCellRange({
        endCell: rangeEndCell ?? selectedCell,
        startCell: selectedCell,
        table,
      }),
    [rangeEndCell, selectedCell, table]
  );

  const renderRow = (row, index, rowProps = {}) => {
    if (!row) return null;

    row.index = index;

    const isHighlighted = row?.original?.row_variant === 'highlight';
    const groupValue = rowGroupAccessor ? row?.original?.[rowGroupAccessor] : undefined;
    const isGroupStart =
      !rowGroupAccessor ||
      index === 0 ||
      rows[index - 1]?.original?.[rowGroupAccessor] !== groupValue;
    const isGroupEnd =
      !rowGroupAccessor ||
      index === rows.length - 1 ||
      rows[index + 1]?.original?.[rowGroupAccessor] !== groupValue;

    return (
      <div
        className={classNames(styles['table-data-tr'], {
          [styles['is-highlighted']]: isHighlighted,
        })}
        key={row.id}
        {...rowProps}
      >
        {row.getVisibleCells().map((cell) => {
          const gradientBackgroundColor = cell.column.columnDef.gradient
            ? `rgba(0, 227, 107, ${cell.getValue() / 2})`
            : null;
          const allowCellWrap = Boolean(cell.column.columnDef?.meta?.allowCellWrap);
          const isGroupedCell = Boolean(rowGroupAccessor && cell.column.columnDef?.rowSpanBy);
          let highlight = false;

          if (typeof cell.column.columnDef.getCellHighlight === 'function') {
            highlight = cell.column.columnDef.getCellHighlight({
              row: row.original,
              rowIndex: row.index,
              value: cell.getValue(),
            });
          }

          return (
            <div
              className={classNames(styles['table-data-td'], {
                [styles['has-cell-wrap']]: allowCellWrap,
                [styles['is-active']]: selectedCell?.id === cell?.id,
                [styles['is-column-hovered']]: hoveredColumnId === cell.column.id,
                [styles['is-group-continuation']]: isGroupedCell && !isGroupEnd,
                [styles['is-group-repeat']]: isGroupedCell && !isGroupStart,
                [styles['is-selected']]: selectedRange[cell?.id],
                [styles[`is-${highlight}`]]: highlight,
              })}
              data-column-id={cell.column.id}
              data-testid={`table-data-cell-${cell.id}`}
              key={cell.id}
              onMouseDown={() => {
                setSelectedCell(cell);
                setRangeEndCell(cell);
              }}
              onMouseEnter={(e) => {
                if (e.buttons === 1 && selectedCell) {
                  setRangeEndCell(cell);
                }
              }}
              style={{
                backgroundColor: gradientBackgroundColor,
                flex: `${cell.column.getSize() ?? 120} 0 auto`,
                width: cell.column.getSize(),
              }}
            >
              <div
                className={classNames(styles['table-data-cell-content'], {
                  [styles['allow-cell-wrap']]: allowCellWrap,
                })}
              >
                {hasPlaceholders && cell.column.getIndex() !== 0 ? (
                  <LoaderSkeleton
                    className={styles['table-data-cell-value-placeholder']}
                    height={12}
                    isAnimated={false}
                    width={90}
                  >
                    <rect height="12" rx="0" ry="0" width="90" x="0" y="0" />
                  </LoaderSkeleton>
                ) : (
                  flexRender(cell.column.columnDef.cell, cell.getContext())
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles['table-data']}>
      <div
        className={classNames({
          [styles['table-data-actions-wrapper']]: prescriptText,
        })}
      >
        {prescriptText}
        <div className={styles['table-data-actions']}>
          {actionButtons?.length > 0 && actionButtons}
          {copyEnabled && (
            <CopyToClipboard
              text="Copy Table"
              value={getCopyTableValue({
                columns: transformedColumns,
                data: rows.map((row) => row.original),
              })}
            />
          )}
        </div>
      </div>

      {isLoading ? (
        <>
          <div className={styles['table-data-thead']}>
            <TableHeader table={table} />
          </div>
          <div>
            {[...Array(size)].map((_, index) => (
              <div className={styles['table-data-tr-loading']} key={`row-${index}`}>
                <LoaderSkeleton height={19.5} width={1280}>
                  <rect height="19.5" rx="2" ry="2" width="100%" x="0" y="0" />
                </LoaderSkeleton>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          {stickyHeader ? (
            <div className={styles['table-data-header-container']} ref={stickyHeaderRef}>
              <TableHeader
                shouldTruncateHeaders={shouldTruncateHeaders}
                showRowCount={!rowGroupAccessor}
                table={table}
              />
            </div>
          ) : null}

          <div
            className={styles['table-data-table']}
            data-testid="table-data-scroll-container"
            onPointerLeave={() => setHoveredColumnId(null)}
            onPointerOver={(event) => {
              const hoveredCell =
                event.target instanceof Element ? event.target.closest('[data-column-id]') : null;
              setHoveredColumnId(hoveredCell?.dataset.columnId ?? null);
            }}
            ref={tableRef}
          >
            {!stickyHeader ? (
              <TableHeader
                shouldTruncateHeaders={shouldTruncateHeaders}
                showRowCount={!rowGroupAccessor}
                table={table}
              />
            ) : null}

            {useWindowScroll ? (
              <WindowVirtualRows renderRow={renderRow} rowHeight={rowHeight} rows={rows} />
            ) : (
              <ElementVirtualRows renderRow={renderRow} rowHeight={rowHeight} rows={rows} />
            )}
          </div>

          {hasFooter ? (
            <div
              className={styles['table-data-footer-container']}
              data-testid="table-data-footer"
              ref={footerRef}
            >
              <div className={styles['table-data-tfoot']}>
                <TableFooter table={table} />
              </div>
            </div>
          ) : null}
        </>
      )}
      {isUpdating && (
        <div className={styles['table-data-loader']}>
          <Loader />
        </div>
      )}
      {!isLoading && !isUpdating && rows.length === 0 && (
        <div className={styles['table-data-empty-message']}>{renderEmptyMessage()}</div>
      )}
    </div>
  );
};

const TableFooter = ({ table }) => {
  const footerGroups = table.getFooterGroups();

  return (
    <>
      {footerGroups.map((footerGroup) => (
        <div className={styles['table-data-tr']} key={footerGroup.id}>
          {footerGroup.headers.map((header, index) => (
            <div
              className={styles['table-data-tf']}
              key={header.id}
              style={{
                flex: index === 0 ? '0 0 250px' : `${header.column.getSize() ?? 120} 0 auto`,
                width: index === 0 ? '250px' : header.column.getSize(),
              }}
            >
              {flexRender(header.column.columnDef.footer, header.getContext())}
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

const TableHeader = ({ shouldTruncateHeaders, showRowCount = true, table }) => {
  if (!table?.getHeaderGroups) return null;

  const headerGroups = table.getHeaderGroups();
  if (!headerGroups?.length) return null;

  return (
    <div className={styles['table-data-thead']}>
      {headerGroups.map((headerGroup) => (
        <div className={styles['table-data-tr']} key={headerGroup.id}>
          {headerGroup.headers?.map((header) => (
            <div
              aria-sort={
                header.column.getIsSorted() === 'desc'
                  ? 'descending'
                  : header.column.getIsSorted() === 'asc'
                    ? 'ascending'
                    : 'none'
              }
              className={classNames(styles['table-data-th'], header.column.className, {
                [styles['is-sortable']]: header.column?.getCanSort(),
              })}
              key={header.id}
              onClick={header.column.getToggleSortingHandler()}
              role="columnheader"
              style={{
                flex: `${header.column.getSize() ?? 120} 0 auto`,
                width: header.column.getSize(),
              }}
              tabIndex={header.column?.getCanSort() ? 0 : -1}
              title={flexRender(header.column.columnDef.header, header.getContext())}
            >
              <p
                className={classNames({
                  [styles['table-data-wrap-header']]: !shouldTruncateHeaders,
                })}
              >
                <OverlayTriggerTooltip
                  content={header.column.columnDef.headerTooltip}
                  placement="top"
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {showRowCount && header.index === 0 && ` (${table.getRowModel().rows.length})`}
                </OverlayTriggerTooltip>
              </p>
              <SortIndicator
                direction={header.column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
                isActive={!!header.column.getIsSorted()}
                isDisabled={!header.column.getCanSort()}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/**
 * Vertically virtualized rows for window-scroll mode. Only the rows within the
 * current viewport (plus overscan) are rendered; the tbody reserves the full
 * height via `getTotalSize()` so the scrollbar and layout stay stable.
 */
const WindowVirtualRows = ({ renderRow, rowHeight, rows }) => {
  const bodyRef = useRef(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // Keep the document offset current while surrounding page content loads and
  // changes height. A stale offset makes the virtualizer render the wrong rows
  // when the table is initially below the viewport.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof window === 'undefined') return undefined;

    const measure = () => {
      const nextScrollMargin = el.getBoundingClientRect().top + window.scrollY;
      setScrollMargin((currentScrollMargin) =>
        currentScrollMargin === nextScrollMargin ? currentScrollMargin : nextScrollMargin
      );
    };

    measure();
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    window.addEventListener('resize', measure);

    const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(measure) : null;
    resizeObserver?.observe(document.body);
    if (el.parentElement) resizeObserver?.observe(el.parentElement);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [rows.length]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    overscan: 6,
    scrollMargin,
  });

  return renderVirtualRows({ bodyRef, renderRow, rows, scrollMargin, virtualizer });
};

const ElementVirtualRows = ({ renderRow, rowHeight, rows }) => {
  const bodyRef = useRef(null);
  const [scrollElement, setScrollElement] = useState(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [useWindowFallback, setUseWindowFallback] = useState(false);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof window === 'undefined') return undefined;

    const nextScrollElement = getScrollParent(el);
    if (!nextScrollElement) {
      setUseWindowFallback(true);
      return undefined;
    }

    setScrollElement(nextScrollElement);

    const measure = () => {
      const scrollElementRect = nextScrollElement.getBoundingClientRect();
      const nextScrollMargin =
        el.getBoundingClientRect().top - scrollElementRect.top + nextScrollElement.scrollTop;
      setScrollMargin((currentScrollMargin) =>
        currentScrollMargin === nextScrollMargin ? currentScrollMargin : nextScrollMargin
      );
    };

    measure();
    nextScrollElement.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);

    const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(measure) : null;
    getElementsAffectingOffset({ element: el, scrollElement: nextScrollElement }).forEach(
      (observedElement) => {
        resizeObserver?.observe(observedElement);
      }
    );

    return () => {
      resizeObserver?.disconnect();
      nextScrollElement.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [rows.length]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    getScrollElement: () => scrollElement,
    overscan: 6,
    scrollMargin,
  });

  if (useWindowFallback) {
    return <WindowVirtualRows renderRow={renderRow} rowHeight={rowHeight} rows={rows} />;
  }

  return renderVirtualRows({ bodyRef, renderRow, rows, scrollMargin, virtualizer });
};

const renderVirtualRows = ({ bodyRef, renderRow, rows, scrollMargin, virtualizer }) => (
  <div
    className={styles['table-data-tbody']}
    data-testid="table-data-body"
    ref={bodyRef}
    style={{
      height: `${virtualizer.getTotalSize()}px`,
    }}
  >
    {virtualizer.getVirtualItems().map((virtualRow) =>
      renderRow(rows[virtualRow.index], virtualRow.index, {
        'data-index': virtualRow.index,
        // Measured so variable-height rows (e.g. wrapped cells) position correctly.
        ref: virtualizer.measureElement,
        style: {
          left: 0,
          position: 'absolute',
          top: 0,
          transform: `translateY(${virtualRow.start - scrollMargin}px)`,
          width: '100%',
        },
      })
    )}
  </div>
);

const getScrollParent = (element) => {
  let parent = element.parentElement;

  while (parent) {
    const { overflowY, position } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
    // A fixed containing block is positioned relative to the viewport, so a
    // scrollable ancestor outside it cannot be this table's scroll element.
    if (position === 'fixed') return null;
    parent = parent.parentElement;
  }

  return null;
};

const getElementsAffectingOffset = ({ element, scrollElement }) => {
  const elements = new Set([scrollElement, ...scrollElement.children]);
  let parent = element.parentElement;

  while (parent && parent !== scrollElement) {
    elements.add(parent);
    parent = parent.parentElement;
  }

  return elements;
};

TableData.defaultProps = {
  copyEnabled: true,
  renderEmptyMessage: () => {},
  size: 15,
  useWindowScroll: true,
};

TableData.propTypes = {
  actionButtons: PropTypes.arrayOf(PropTypes.node),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string,
      Cell: PropTypes.func,
      disableSortBy: PropTypes.bool,
      format: PropTypes.string,
      Header: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]),
      meta: PropTypes.shape({
        allowCellWrap: PropTypes.bool,
      }),
    })
  ).isRequired,
  copyEnabled: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasPlaceholders: PropTypes.bool,
  isLoading: PropTypes.bool,
  isUpdating: PropTypes.bool,
  onSortedTableDataChange: PropTypes.func,
  prescriptText: PropTypes.node,
  renderEmptyMessage: PropTypes.func,
  rowHeight: PropTypes.number,
  setDownloadData: PropTypes.func,
  shouldTruncateHeaders: PropTypes.bool,
  size: PropTypes.number,
  stickyHeader: PropTypes.bool,
  useWindowScroll: PropTypes.bool,
};

export default TableData;
